import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { setPref } from '../utils/userPreferences'

interface ChecklistItem {
  id: string
  label: string
  description: string
  completed: boolean
  action: () => void
}

interface OnboardingChecklistProps {
  hasClients: boolean
  hasDeals: boolean
  hasProfile: boolean
  hasProspects?: boolean
  hasContracts?: boolean
  hasTeamMembers?: boolean
  /** The agency's commission split is on file; without it no commission can be booked. */
  hasCommissionSettings?: boolean
  userName?: string
}

const CHECKLIST_DISMISSED_KEY = 'reppr_onboarding_checklist_dismissed'

export function OnboardingChecklist({
  hasClients,
  hasDeals,
  hasProfile,
  hasProspects = false,
  hasContracts = false,
  hasTeamMembers = false,
  hasCommissionSettings = false,
  userName = 'there'
}: OnboardingChecklistProps) {
  const navigate = useNavigate()
  const [dismissed, setDismissed] = useState(false)
  const [isCollapsed, setIsCollapsed] = useState(false)

  // Check if user dismissed the checklist
  useEffect(() => {
    const isDismissed = localStorage.getItem(CHECKLIST_DISMISSED_KEY)
    if (isDismissed === 'true') {
      setDismissed(true)
    }
  }, [])

  const checklistItems: ChecklistItem[] = [
    {
      id: 'client',
      label: 'Add your first client',
      description: 'Start tracking your clients',
      completed: hasClients,
      action: () => navigate('/clients/new')
    },
    {
      id: 'deal',
      label: 'Create a deal',
      description: 'Track opportunities and negotiations',
      completed: hasDeals,
      action: () => navigate('/deals/new')
    },
    {
      id: 'prospect',
      label: 'Track a prospect',
      description: 'Add athletes you\'re working to sign',
      completed: hasProspects,
      action: () => navigate('/prospects/new')
    },
    {
      id: 'contract',
      label: 'Set up a contract',
      description: 'Document and track player agreements',
      completed: hasContracts,
      action: () => navigate('/contracts/new')
    },
    {
      id: 'commission',
      label: 'Set your commission split and rate',
      description: 'Nothing books to the ledger until the agency split is on file; the rate applies to contracts built from OverTheCap',
      completed: hasCommissionSettings,
      action: () => navigate('/commissions')
    },
    {
      id: 'team',
      label: 'Invite a team member',
      description: 'Bring your agents and assistants onboard',
      completed: hasTeamMembers,
      action: () => navigate('/team')
    },
    {
      id: 'profile',
      label: 'Complete your profile',
      description: 'Add your contact info and preferences',
      completed: hasProfile,
      action: () => navigate('/settings')
    },
  ]

  const completedCount = checklistItems.filter(item => item.completed).length
  const totalCount = checklistItems.length
  // Auto-hide when 5 of 6 complete (compliance not required)
  const allCompleted = completedCount >= totalCount - 1
  const progressPercent = Math.round((completedCount / totalCount) * 100)

  const handleDismiss = () => {
    localStorage.setItem(CHECKLIST_DISMISSED_KEY, 'true')
    setPref('checklist_dismissed', true)
    setDismissed(true)
  }

  // Don't render if dismissed or all items completed
  if (dismissed || allCompleted) {
    return null
  }

  return (
    <div style={{
      background: 'linear-gradient(135deg, rgba(200, 164, 92, 0.1) 0%, rgba(200, 164, 92, 0.05) 100%)',
      border: '1px solid rgba(200, 164, 92, 0.2)',
      borderRadius: 0,
      padding: '20px',
      marginBottom: '24px'
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        marginBottom: isCollapsed ? 0 : '16px'
      }}>
        <div style={{ flex: 1 }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            marginBottom: '4px'
          }}>
            <span style={{ fontSize: '1.25rem' }}>👋</span>
            <h3 style={{
              margin: 0,
              fontSize: '1rem',
              fontWeight: 600,
              color: '#fff'
            }}>
              Welcome, {userName}!
            </h3>
          </div>
          <p style={{
            margin: 0,
            fontSize: '0.875rem',
            color: 'rgba(255, 255, 255, 0.6)'
          }}>
            Complete these steps to get the most out of Reppr
          </p>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {/* Progress badge */}
          <span style={{
            background: 'rgba(200, 164, 92, 0.2)',
            color: '#C8A45C',
            padding: '4px 10px',
            borderRadius: 0,
            fontSize: '0.8125rem',
            fontWeight: 600
          }}>
            {completedCount}/{totalCount}
          </span>

          {/* Collapse toggle */}
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'rgba(255, 255, 255, 0.5)',
              cursor: 'pointer',
              padding: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            title={isCollapsed ? 'Expand' : 'Collapse'}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              style={{
                transform: isCollapsed ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s'
              }}
            >
              <polyline points="18 15 12 9 6 15"></polyline>
            </svg>
          </button>

          {/* Dismiss button */}
          <button
            onClick={handleDismiss}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'rgba(255, 255, 255, 0.5)',
              cursor: 'pointer',
              padding: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            title="Dismiss checklist"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
      </div>

      {/* Progress bar */}
      {!isCollapsed && (
        <>
          <div style={{
            height: '4px',
            background: 'rgba(255, 255, 255, 0.1)',
            borderRadius: 0,
            marginBottom: '16px',
            overflow: 'hidden'
          }}>
            <div style={{
              height: '100%',
              width: `${progressPercent}%`,
              background: '#C8A45C',
              borderRadius: 0,
              transition: 'width 0.3s ease'
            }} />
          </div>

          {/* Checklist items */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {checklistItems.map(item => (
              <button
                key={item.id}
                onClick={item.action}
                disabled={item.completed}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '12px',
                  background: item.completed
                    ? 'rgba(78, 154, 106, 0.1)'
                    : 'rgba(255, 255, 255, 0.03)',
                  border: item.completed
                    ? '1px solid rgba(78, 154, 106, 0.2)'
                    : '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: 0,
                  cursor: item.completed ? 'default' : 'pointer',
                  textAlign: 'left',
                  width: '100%',
                  transition: 'all 0.2s ease'
                }}
              >
                {/* Checkbox */}
                <div style={{
                  width: '20px',
                  height: '20px',
                  borderRadius: 0,
                  border: item.completed
                    ? '2px solid #4E9A6A'
                    : '2px solid rgba(255, 255, 255, 0.3)',
                  background: item.completed ? '#4E9A6A' : 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  {item.completed && (
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                  )}
                </div>

                {/* Content */}
                <div style={{ flex: 1 }}>
                  <div style={{
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    color: item.completed ? 'rgba(255, 255, 255, 0.5)' : '#fff',
                    textDecoration: item.completed ? 'line-through' : 'none'
                  }}>
                    {item.label}
                  </div>
                  <div style={{
                    fontSize: '0.8125rem',
                    color: 'rgba(255, 255, 255, 0.4)',
                    marginTop: '2px'
                  }}>
                    {item.description}
                  </div>
                </div>

                {/* Arrow for incomplete items */}
                {!item.completed && (
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="rgba(255, 255, 255, 0.4)"
                    strokeWidth="2"
                  >
                    <polyline points="9 18 15 12 9 6"></polyline>
                  </svg>
                )}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  )
}
